import {
  BellIcon,
  CalculatorIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  HomeIcon,
  LinkIcon,
  MapIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
  UserIcon,
  WrenchScrewdriverIcon,
  ArrowUpOnSquareStackIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  DocumentPlusIcon,
  RectangleGroupIcon,
  TicketIcon,
  NewspaperIcon,
  Square2StackIcon,
  SwatchIcon,
} from "@heroicons/react/24/solid";

type TPartnerSideTabSchema =
  | {
      icon: JSX.Element;
      name: string;
      path: string;
      code: string;
      subTabs?: never;
    }
  | {
      icon: JSX.Element;
      name: string;
      path?: never;
      code: string;
      subTabs: { name: string; path: string; code: string }[];
    };

const PARTNERS_TABS: TPartnerSideTabSchema[] = [
  {
    icon: <HomeIcon />,
    name: "Home",
    path: "/home",
    code: "home",
  },
  {
    icon: <Squares2X2Icon />,
    name: "Dashboards",
    code: "dashboards",
    subTabs: [
      {
        name: "Card Types",
        path: "/dashboards/card-types",
        code: "card-types",
      },
      {
        name: "Cards",
        path: "/dashboards/cards",
        code: "cards",
      },
      {
        name: "Dashboard",
        path: "/dashboards/dashboard",
        code: "dashboard",
      },
    ],
  },
  {
    icon: <UserIcon />,
    name: "Profile",
    path: "/profile",
    code: "profile",
  },
  {
    icon: <WrenchScrewdriverIcon />,
    name: "User Access",
    code: "user-access",
    subTabs: [
      {
        name: "Roles",
        path: "/user-access/roles",
        code: "roles",
      },
      {
        name: "Users",
        path: "/user-access/users",
        code: "users",
      },
      {
        name: "Permissions",
        path: "/user-access/permissions",
        code: "permissions",
      },
    ],
  },

  {
    icon: <DocumentDuplicateIcon />,
    name: "Customer",
    path: "/customer",
    code: "customer",
  },
  {
    icon: <PuzzlePieceIcon />,
    name: "Leads",
    path: "/leads",
    code: "leads",
  },
  {
    icon: <CalculatorIcon />,
    name: "Accounting",
    code: "accounting",
    subTabs: [
      {
        name: "Accounts",
        path: "/accounting/accounts",
        code: "accounts",
      },
      {
        name: "Transactions",
        path: "/accounting/transactions",
        code: "transactions",
      },
      {
        name: "Cash Deposits",
        path: "/accounting/cash-deposits",
        code: "cash-deposits",
      },
    ],
  },
  {
    icon: <TicketIcon />,
    name: "Product Config",
    code: "product-config",
    subTabs: [
      {
        name: "Products",
        code: "products",
        path: "/product-config/products",
      },
      {
        name: "Product Features",
        code: "product-features",
        path: "/product-config/product-features",
      },
    ],
  },

  {
    icon: <DocumentTextIcon />,
    name: "Plan Config",
    code: "plan-config",
    subTabs: [
      {
        name: "Plans",
        path: "/plan-config/plans",
        code: "plans",
      },
      {
        name: "Policies",
        code: "policies",
        path: "/plan-config/policies",
      },
      {
        name: "Policy Features",
        code: "policy-features",
        path: "/plan-config/policy-features",
      },
      {
        name: "Combos",
        code: "combos",
        path: "/plan-config/combos",
      },
      {
        name: "Loading",
        code: "loading",
        path: "/plan-config/loading",
      },

      {
        name: "Block Areas",
        code: "block-areas",
        path: "/plan-config/block-areas",
      },
    ],
  },
  {
    icon: <RectangleGroupIcon />,
    name: "Riders",
    path: "/riders",
    code: "riders",
  },
  {
    icon: <ArrowUpOnSquareStackIcon />,
    name: "Rater Config",
    code: "rater-config",
    path: "/rater-config",
  },

  {
    icon: <DocumentPlusIcon />,
    name: "Proposal Management",
    code: "proposal-management",
    subTabs: [
      {
        name: "Proposal Bulk Upload",
        path: "/proposal-management/proposal-bulk-upload",
        code: "proposal-bulk-upload",
      },
      {
        name: "Certificates",
        path: "/proposal-management/certificates?coi-status=3",
        code: "certificates",
      },
    ],
  },
  {
    icon: <NewspaperIcon />,
    name: "Quote Management",
    code: "quote-management",
    subTabs: [
      {
        name: "RFQ",
        path: "/quote-management/rfq",
        code: "rfq",
      },
      {
        name: "QCR",
        path: "/quote-management/qcr",
        code: "qcr",
      },
      {
        name: "Placement Slip",
        path: "/quote-management/placement-slip",
        code: "placement-slip",
      },
      {
        name: "Sales Template",
        path: "/quote-management/sales-template",
        code: "sales-template",
      },
    ],
  },
  {
    icon: <DocumentTextIcon />,
    name: "Report Management",
    code: "report-management",
    subTabs: [
      {
        name: "Reports",
        path: "/report-management/reports",
        code: "reports",
      },
    ],
  },
  {
    icon: <LinkIcon />,
    name: "Journey Config",
    code: "journey-config",
    subTabs: [
      {
        name: "Journey Links",
        path: "/journey-config/journey-links",
        code: "journey-links",
      },
    ],
  },

  {
    icon: <BanknotesIcon />,
    name: "Commission",
    code: "commission",
    subTabs: [
      {
        name: "Commissions",
        path: "/commission/commissions",
        code: "commissions",
      },
      {
        name: "Commission Setting",
        path: "/commission/commission-setting",
        code: "commission-setting",
      },
    ],
  },
  {
    icon: <Square2StackIcon />,
    name: "Landing Page",
    code: "landing-page",
    subTabs: [
      {
        name: "Broker Landing Page",
        path: "/landing-page/broker-landing-page",
        code: "broker-landing-page",
      },
      {
        name: "Partner Landing Page",
        path: "/landing-page/partner-landing-page",
        code: "partner-landing-page",
      },
    ],
  },
  {
    icon: <BellIcon />,
    name: "Notifications",
    code: "notifications",
    subTabs: [
      {
        name: "Alerts",
        path: "/notifications/alerts",
        code: "alerts",
      },
      {
        name: "Alert Templates",
        path: "/notifications/alert-templates",
        code: "alert-templates",
      },
      {
        name: "Default Alert Template",
        path: "/notifications/default-alert-template",
        code: "default alert template",
      },
    ],
  },
  {
    icon: <MapIcon />,
    name: "Activity Log",
    path: "/activity-log",
    code: "activity-log",
  },

  {
    icon: <Cog6ToothIcon />,
    name: "Settings",
    code: "settings",
    subTabs: [
      {
        name: "Themes",
        path: "/settings/themes",
        code: "themes",
      },
      {
        name: "Api Tokens",
        path: "/settings/api-tokens",
        code: "api-tokens",
      },
    ],
  },
  {
    icon: <SwatchIcon />,
    name: "Masters",
    code: "masters",
    subTabs: [
      {
        path: "/masters/insurers",
        name: "Insurers",
        code: "insurers",
      },

      {
        name: "Relations",
        path: "/masters/relations",
        code: "relations",
      },
      {
        name: "Payment Mode",
        path: "/masters/payment-mode",
        code: "payment-mode",
      },
      {
        name: "Payment Gateways",
        path: "/masters/payment-gateways",
        code: "payment-gateways",
      },
      {
        name: "Worker Types",
        path: "/masters/worker-types",
        code: "worker-types",
      },
      {
        path: "/masters/business-types",
        name: "Business Types",
        code: "business-types",
      },
      {
        path: "/masters/bank-branches",
        name: "Bank Branches",
        code: "bank-branches",
      },
      {
        name: "Feature Master",
        path: "/masters/feature-master",
        code: "feature-master",
      },
      {
        name: "Feature Categories",
        path: "/masters/feature-categories",
        code: "feature-categories",
      },
      {
        name: "Species",
        path: "/masters/species",
        code: "species",
      },
      {
        name: "Cattle Types",
        path: "/masters/cattle-types",
        code: "cattle-types",
      },
      {
        name: "Entity",
        path: "/masters/entity",
        code: "entity",
      },
      {
        name: "Breeds",
        path: "/masters/breeds",
        code: "breeds",
      },
      {
        name: "Cattle Breeds",
        path: "/masters/cattle-breeds",
        code: "cattle-breeds",
      },
      {
        name: "Occupations",
        path: "/masters/occupations",
        code: "occupations",
      },
      {
        name: "Occupancy",
        path: "/masters/occupancy-type",
        code: "occupancy-type",
      },
      {
        name: "Specialization",
        path: "/masters/specialization",
        code: "specialization",
      },
      {
        name: "Commodity Types",
        path: "/masters/commodity-types",
        code: "commodity-types",
      },
      {
        name: "Incoterms",
        path: "/masters/incoterms",
        code: "incoterms",
      },
      {
        name: "Business Nature",
        path: "/masters/business-nature",
        code: "business-nature",
      },
      {
        name: "Event Types",
        path: "/masters/event-types",
        code: "event-types",
      },
      {
        name: "Currency Type",
        path: "/masters/currency-type",
        code: "currency-type",
      },
      {
        name: "Packaging Types",
        path: "/masters/packaging-types",
        code: "packaging-types",
      },
      {
        name: "Segments",
        path: "/masters/segments",
        code: "segments",
      },
      {
        name: "Gadgets",
        path: "/masters/gadgets",
        code: "gadgets",
      },
      {
        name: "Claim Types",
        path: "/masters/claim-types",
        code: "claim-types",
      },
      {
        name: "Claim Expenses",
        path: "/masters/claim-expenses",
        code: "claim-expenses",
      },
      {
        name: "Claim Stages",
        path: "/masters/claim-stages",
        code: "claim-stages",
      },
      {
        name: "Property Coverages",
        path: "/masters/property-coverages",
        code: "property-coverages",
      },
      {
        name: "Cancellation Reasons",
        path: "/masters/cancellation-reasons",
        code: "cancellation-reasons",
      },
      {
        name: "Claim Settlement Type",
        path: "/masters/claim-settlement-type",
        code: "claim-settlement-type",
      },
    ],
  },
];

export { PARTNERS_TABS };
